import {
  CSRF_HEADER,
  type HeadingAnchor,
  headingFragment,
  headingKeys,
  type ProjectDetail,
  type WikiPageView,
  wikiSlug,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, BookOpen, ChevronRight, Download, Plus, SquarePen } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/EmptyState";
import { Markdown, titleHeadingIndex } from "@/components/markdown/Markdown";
import { parseMarkdown } from "@/components/markdown/parse";
import { Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { useDocumentTitle } from "@/hooks/use-document-title";
import { api } from "../api.ts";
import { noContent, ok } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import { t } from "../i18n/i18n.ts";
import { can, canChange } from "../projects/capabilities.ts";
import { useFolders } from "../projects/folders.ts";
import { useFolderParam } from "../projects/KnowledgeTree.tsx";
import { useProject } from "../projects/queries.ts";
import {
  changeSetPath,
  editPagePath,
  Link,
  navigate,
  newPagePath,
  PROJECTS_PATH,
  pageComparePath,
  projectConversationPath,
  projectHomePath,
  type Route,
  useSearch,
  wikiChangesPath,
  wikiPath,
  wikiTabPath,
} from "../router.tsx";
import { useSession } from "../session.tsx";
import { ChangeSetReview } from "./ChangeSetReview.tsx";
import { ChangesInbox } from "./ChangesInbox.tsx";
import { ReadSinceLine, ReadSinceMarks, usePageReadSince } from "./history.tsx";
import { LegacyDraftLink } from "./LegacyDraftLink.tsx";
import { PageEditor } from "./PageEditor.tsx";
import { FolderNeighbours, PageBreadcrumb } from "./PageNavigation.tsx";
import { usePagePassages } from "./PagePassages.tsx";
import { useHeadingAside } from "./PageThreads.tsx";
import {
  PendingHintsContext,
  PendingSwitch,
  usePageHints,
  usePendingHintsState,
  usePendingShortcut,
} from "./PendingHints.tsx";
import {
  useChangesInbox,
  useDeclassifications,
  useWikiExport,
  useWikiIndex,
  useWikiPage,
  wikiKeys,
} from "./queries.ts";
import { RestoreDialog, type RestoreTarget } from "./RestoreDialog.tsx";
import { useWikiFolderIndex } from "./structure.ts";
import { PageCompare } from "./WikiCompare.tsx";
import { WikiDocs } from "./WikiDocs.tsx";
import { WikiFolderIndex } from "./WikiFolderIndex.tsx";
import { PageHistory, WikiActivity } from "./WikiHistory.tsx";
import { NEEDS_CHECKING_FILTER, WikiNeedsChecking, WikiOverview } from "./WikiOverview.tsx";

const formatDate = formatDateTime;

const Loading = () => <Skeleton className="h-24 w-full" />;

type WikiRoute = Extract<
  Route,
  {
    name:
      | "wiki"
      | "wikiNew"
      | "wikiEdit"
      | "wikiDraft"
      | "wikiChange"
      | "wikiHistory"
      | "wikiCompare";
  }
>;

/**
 * A project's wiki: its pages (index, log, one page), the review queue, declassifications. The
 * tab, the page and the draft under review or in the editor are all in the URL; a draft lives
 * under Review, never under Pages.
 */
export function WikiView({ route }: { route: WikiRoute }) {
  // A standalone page (kept for the wiki's own tests); the app shows it as a project home tab.
  const project = useProject(route.projectId);
  if (project.isPending) {
    return (
      <Page width="wide">
        <Loading />
      </Page>
    );
  }
  // A failed refetch keeps what is shown (and any unsaved edit in it): only a first load fails.
  if (project.isError && !project.data) {
    return (
      <Page width="narrow">
        <Notice tone="danger">{project.error.message}</Notice>
      </Page>
    );
  }
  return <Wiki project={project.data} route={route} />;
}

/**
 * The wiki's toolbar destinations and content. `embedded`: inside the project home's Wiki tab, which carries the
 * project header; standalone otherwise, with its own page header.
 */
export function Wiki({
  project,
  route,
  embedded = false,
}: {
  project: ProjectDetail;
  route: WikiRoute;
  embedded?: boolean;
}) {
  useTranslation();
  useDocumentTitle(`${t("wiki.title")} · ${project.name}`);
  // What the caller may do, from the capabilities the server computed (never their role).
  const mayDeclassify = can(project, "declassify");
  const canWrite = canChange(project, "wiki.draft");
  const tab = route.name === "wiki" ? route.tab : "changes";
  // On the inbox, the toolbar shares its sized queue read: one fetch, one count with the rows.
  const count =
    useChangesInbox(
      project,
      can(project, "wiki.view_drafts"),
      route.name === "wiki" && tab === "changes",
    ).inbox?.count ?? 0;
  const pageId = route.name === "wiki" ? route.pageId : null;
  // Pending-change hints (wiki-collaboration-ux D9): the switch on Pages, Shift+P there; it
  // drives a page's hints and the Pages panel's markers (#411), so the overview has it too.
  const hints = usePendingHintsState(project);
  const onPages = route.name === "wiki" && tab === "pages";
  usePendingShortcut(hints, onPages);
  const content = (
    <div className="flex min-h-0 flex-1 flex-col">
      <section
        aria-label={t("wiki.docs.toolbar")}
        className="flex shrink-0 flex-wrap items-center gap-1 border-b border-line px-3 py-1.5"
      >
        {tab !== "pages" || route.name === "wikiHistory" || route.name === "wikiCompare" ? (
          <Button asChild variant="ghost" size="sm">
            <Link href={wikiPath(project.id)}>{t("wiki.docs.pages")}</Link>
          </Button>
        ) : null}
        <div className="flex-1" />
        {onPages ? <PendingSwitch hints={hints} /> : null}
        {can(project, "wiki.view_drafts") ? (
          <Button asChild variant={tab === "changes" ? "secondary" : "ghost"} size="sm">
            <Link
              href={wikiChangesPath(project.id)}
              aria-current={tab === "changes" && route.name === "wiki" ? "page" : undefined}
            >
              {t("wiki.inbox.title")}
              {count > 0 ? <span className="text-fg-muted tabular-nums">{count}</span> : null}
            </Link>
          </Button>
        ) : null}
        {can(project, "wiki.view_drafts") ? (
          <Button asChild variant={tab === "activity" ? "secondary" : "ghost"} size="sm">
            <Link
              href={wikiTabPath(project.id, "activity")}
              aria-current={tab === "activity" ? "page" : undefined}
            >
              {t("wiki.changes.activity")}
            </Link>
          </Button>
        ) : null}
        {mayDeclassify ? (
          <Button asChild variant={tab === "declassifications" ? "secondary" : "ghost"} size="sm">
            <Link href={wikiTabPath(project.id, "declassifications")}>
              {t("wiki.tabs.declassifications")}
            </Link>
          </Button>
        ) : null}
        {can(project, "wiki.export") ? <ExportButton projectId={project.id} /> : null}
      </section>
      {route.name === "wikiChange" ? (
        <ChangeSetReview key={route.changeSetId} project={project} id={route.changeSetId} />
      ) : route.name === "wikiHistory" ? (
        <div data-scroll-owner="page" className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-8">
          <PageHistory project={project} pageId={route.pageId} />
        </div>
      ) : route.name === "wikiCompare" ? (
        <div data-scroll-owner="page" className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-8">
          <PageCompare
            key={`${route.pageId}:${route.from}:${route.to}`}
            project={project}
            pageId={route.pageId}
            from={route.from}
            to={route.to}
          />
        </div>
      ) : route.name === "wikiNew" || route.name === "wikiEdit" ? (
        // The editor's sticky action bar is outside the area focused fields scroll into.
        <div
          data-scroll-owner="page"
          className="min-h-0 flex-1 scroll-pb-24 overflow-y-auto p-5 sm:p-8"
        >
          <PageEditor
            key={route.name === "wikiEdit" ? route.pageId : `new:${route.folderId ?? ""}`}
            project={project}
            pageId={route.name === "wikiEdit" ? route.pageId : null}
            folderId={route.name === "wikiNew" ? route.folderId : null}
          />
        </div>
      ) : route.name === "wikiDraft" ? (
        <div data-scroll-owner="page" className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-8">
          <LegacyDraftLink key={route.draftId} project={project} draftId={route.draftId} />
        </div>
      ) : tab === "pages" ? (
        <PendingHintsContext.Provider value={hints}>
          <Pages
            project={project}
            pageId={pageId}
            canWrite={canWrite}
            onEdit={(page) => navigate(editPagePath(project.id, page.id))}
            onNew={(folderId) => navigate(newPagePath(project.id, folderId))}
          />
        </PendingHintsContext.Provider>
      ) : (
        <div data-scroll-owner="page" className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-8">
          {tab === "activity" && can(project, "wiki.view_drafts") ? (
            <WikiActivity projectId={project.id} />
          ) : tab === "declassifications" && mayDeclassify ? (
            <Declassifications projectId={project.id} />
          ) : (
            <ChangesInbox
              project={project}
              view={(route.name === "wiki" && route.view) || "to-decide"}
            />
          )}
        </div>
      )}
    </div>
  );
  if (embedded) {
    return content;
  }
  return (
    <Page width="wide" className="gap-6">
      <header className="flex flex-col gap-1.5">
        <nav aria-label={t("project.breadcrumb")} className="text-xs">
          <ol className="flex flex-wrap items-center gap-1 text-fg-muted">
            <li className="flex items-center gap-1">
              <Link
                href={PROJECTS_PATH}
                className="underline-offset-4 hover:text-fg hover:underline"
              >
                {t("nav.projects")}
              </Link>
              <ChevronRight aria-hidden="true" className="size-3" />
            </li>
            <li data-esi-content="projects" className="flex items-center gap-1">
              <Link
                href={projectHomePath(project.id, "wiki")}
                className="underline-offset-4 hover:text-fg hover:underline"
              >
                {project.name}
              </Link>
              <ChevronRight aria-hidden="true" className="size-3" />
            </li>
          </ol>
        </nav>
        <h1 className="text-xl font-semibold tracking-tight text-fg">{t("wiki.title")}</h1>
      </header>
      {content}
    </Page>
  );
}

function Pages(props: {
  project: ProjectDetail;
  pageId: string | null;
  canWrite: boolean;
  onEdit: (page: WikiPageView) => void;
  /** A new page, in the folder being browsed or of the page being read. */
  onNew: (folderId: string | null) => void;
}) {
  const { project, canWrite, onEdit, onNew } = props;
  const index = useWikiIndex(project.id);
  const [folder] = useFolderParam();
  // Never a page the user did not choose: the bare URL is the overview (spec `project-wiki`).
  const { pageId } = props;
  // `?folder=` with no page open: that folder's index, even in a wiki with no readable page; a
  // folder the list does not hold (of Files, or deleted since) falls back to the overview.
  const folders = useFolders(project.id);
  const folderIndex = useWikiFolderIndex(project.id, folder ?? "");
  const shownFolder = !pageId && folder ? folderIndex : null;
  // `?filter=needs-checking` (Overview's Need checking figure): the pages to check, with why.
  const needsChecking = useSearch().get("filter") === NEEDS_CHECKING_FILTER;
  const newPage = () =>
    onNew(
      folder ??
        (pageId ? (index.data?.pages.find((p) => p.id === pageId)?.folderId ?? null) : null),
    );
  return (
    <WikiDocs
      project={project}
      pageId={pageId}
      // An empty wiki offers New page in its explanation, once.
      canWrite={canWrite && (pageId !== null || !!index.data?.pages.length || shownFolder !== null)}
      onNew={newPage}
    >
      {pageId ? (
        <WikiPage project={project} pageId={pageId} canWrite={canWrite} onEdit={onEdit} />
      ) : index.isPending || (folder && folders.isPending) ? (
        <Loading />
      ) : index.isError ? (
        <Notice tone="danger">{index.error.message}</Notice>
      ) : shownFolder ? (
        <WikiFolderIndex project={project} index={shownFolder} />
      ) : needsChecking && index.data ? (
        <WikiNeedsChecking project={project} pages={index.data.pages} />
      ) : index.data?.pages.length ? (
        <WikiOverview project={project} pages={index.data.pages} />
      ) : (
        <WikiEmpty project={project} canWrite={canWrite} onNewPage={newPage} />
      )}
    </WikiDocs>
  );
}

/**
 * The Pages view of a wiki with no page the viewer can read (spec `project-wiki` "Wiki empty
 * state"): what the wiki is for, then what the viewer may do next. Decided from the list the
 * server filtered, so it says nothing about pages the viewer cannot read.
 */
function WikiEmpty(props: { project: ProjectDetail; canWrite: boolean; onNewPage: () => void }) {
  const { project, canWrite, onNewPage } = props;
  // Drafts may wait with no published page: those who may review still see how many they must
  // act on. Other review items count too, as in the knowledge tree's Wiki section.
  const needs = useChangesInbox(project, canWrite).inbox?.toDecide;
  const drafts = canWrite ? (needs?.changeSets.length ?? 0) : 0;
  const items = canWrite ? (needs?.items.length ?? 0) : 0;
  return (
    <EmptyState
      icon={BookOpen}
      title={t("wiki.empty.title")}
      className="max-w-lg py-4 md:py-8"
      action={
        <div className="flex flex-col items-start gap-4 pt-1">
          {/* Writing needs an unfrozen project too: nothing to start in a frozen one. */}
          {!project.frozenAt ? (
            <div className="flex flex-wrap gap-2">
              {canWrite ? (
                <Button size="sm" variant="outline" onClick={onNewPage}>
                  <Plus aria-hidden="true" />
                  {t("wiki.newPage")}
                </Button>
              ) : null}
              {project.frozenAt ? null : (
                <Button asChild size="sm" variant="outline">
                  <Link href={projectConversationPath(project.id, "new")}>
                    <SquarePen aria-hidden="true" strokeWidth={1.75} />
                    {t("wiki.empty.startConversation")}
                  </Link>
                </Button>
              )}
            </div>
          ) : null}
          <div className="flex flex-col items-start gap-1.5 text-sm">
            {drafts + items > 0 ? (
              <Link
                href={wikiChangesPath(project.id)}
                className="text-accent-text underline-offset-4 hover:underline"
              >
                {items === 0
                  ? t("wiki.empty.waiting", { count: drafts })
                  : t("wiki.reviewCount", { count: drafts + items })}
              </Link>
            ) : null}
            <Link
              href={projectHomePath(project.id, "files")}
              className="inline-flex items-center gap-1 text-fg-secondary underline-offset-4 hover:text-fg hover:underline"
            >
              {t("wiki.empty.sources")}
              <ArrowRight aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      }
    >
      {/* Only those who may write are told to write the first page (#252). */}
      {canWrite ? t("wiki.empty.body") : t("wiki.empty.bodyRead")}
    </EmptyState>
  );
}

function WikiPage(props: {
  project: ProjectDetail;
  pageId: string;
  canWrite: boolean;
  onEdit: (page: WikiPageView) => void;
}) {
  const { project, pageId, canWrite, onEdit } = props;
  const search = useSearch();
  const version = Number(search.get("version")) || undefined;
  const page = useWikiPage(project.id, pageId, version);
  // The comment affordance beside each heading (wiki-collaboration D17).
  const aside = useHeadingAside(project, {
    id: pageId,
    markdown: page.data?.markdown ?? "",
    version: page.data?.version ?? 0,
    shownVersion: page.data?.shownVersion ?? -1,
  });
  const [restore, setRestore] = useState<RestoreTarget | null>(null);
  const restoreTrigger = useRef<HTMLButtonElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: async (): Promise<string | null> => {
      const res = await api.api.projects[":projectId"].wiki.pages[":pageId"].$delete(
        { param: { projectId: project.id, pageId } },
        { headers: { [CSRF_HEADER]: csrfToken } },
      );
      // 202: the trust level leaves the deletion to another person's review.
      if ((res.status as number) === 202) {
        return ((await res.json()) as { changeSetId: string }).changeSetId;
      }
      await noContent(res);
      return null;
    },
    onSuccess: (inReview) => {
      void queryClient.invalidateQueries({ queryKey: wikiKeys.all(project.id) });
      navigate(inReview ? changeSetPath(project.id, inReview) : wikiPath(project.id));
    },
  });
  if (page.isError) {
    return <Notice tone="danger">{page.error.message}</Notice>;
  }
  if (!page.data) {
    return <Loading />;
  }
  return (
    <WikiPageArticle
      project={project}
      page={page.data}
      canWrite={canWrite}
      onEdit={onEdit}
      aside={aside}
      body={body}
      remove={remove}
      restore={restore}
      setRestore={setRestore}
      restoreTrigger={restoreTrigger}
    />
  );
}

/** The page as it reads, with its passage threads (wiki-collaboration-ux S4). */
function WikiPageArticle(props: {
  project: ProjectDetail;
  page: WikiPageView;
  canWrite: boolean;
  onEdit: (page: WikiPageView) => void;
  aside: ReturnType<typeof useHeadingAside>;
  body: React.RefObject<HTMLDivElement | null>;
  remove: { mutate: () => void; isError: boolean; error: Error | null };
  restore: RestoreTarget | null;
  setRestore: (target: RestoreTarget | null) => void;
  restoreTrigger: React.RefObject<HTMLButtonElement | null>;
}) {
  const { project, canWrite, onEdit, aside, body, remove, restore, setRestore, restoreTrigger } =
    props;
  const p = props.page;
  const blocks = parseMarkdown(p.markdown);
  const titleIndex = titleHeadingIndex(blocks, p.title);
  // "What changed since you last read it" (S5, D18): offered once per visit, marks on Show.
  const readSince = usePageReadSince(project.id, p);
  // Held per page and version: another page opens unmarked.
  const sinceKey = `${p.id}:${p.version}`;
  const [showSince, setShowSince] = useState<string | null>(null);
  const since = readSince.since;
  const marking = showSince === sinceKey && since !== null;
  const passages = usePagePassages(project, p, body, titleIndex, marking);
  const hints = usePageHints(project, p.id, p.shownVersion === p.version);
  const headingAside = (anchor: HeadingAnchor) => {
    const bar = hints.bar(anchor.slug);
    const own = aside.render?.(anchor) ?? null;
    return bar || own ? (
      <>
        {bar}
        {own}
      </>
    ) : null;
  };
  const titleHeading = headingKeys(blocks).find((h) => h.block === titleIndex);
  const byTitle = new Map(
    p.links.flatMap((l) => (l.page ? [[wikiSlug(l.title), l.page.id] as const] : [])),
  );
  const resolve = (title: string) => {
    const id = byTitle.get(wikiSlug(title));
    return id ? wikiPath(project.id, id) : null;
  };
  const reading = (
    <Markdown
      headingPrefix="wiki-section"
      omitTitleHeading={p.title}
      markdown={p.markdown}
      headingAside={headingAside}
      inlineAt={passages.inlineAt}
      resolve={resolve}
    />
  );
  return (
    <article data-esi-content="wiki" className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      <header className="flex flex-col gap-2">
        <PageBreadcrumb projectId={project.id} page={p} />
        <h2
          id={titleHeading ? `wiki-section-${headingFragment(titleHeading)}` : undefined}
          className="scroll-mt-6 text-2xl font-semibold tracking-tight text-balance text-fg"
        >
          {p.title}
        </h2>
        <p className="text-xs text-fg-muted">
          {t(`wiki.pageTypes.${p.pageType}`)} · {t("wiki.version", { version: p.shownVersion })}
          {p.status === "processing" ? ` · ${t("wiki.processing")}` : ""}
        </p>
        {p.newerVersion ? (
          <Notice
            tone="info"
            action={
              <div className="flex flex-wrap gap-2">
                <Button asChild size="xs" variant="outline">
                  <Link href={wikiPath(project.id, p.id)}>{t("wiki.openCurrent")}</Link>
                </Button>
                <Button asChild size="xs" variant="outline">
                  <Link href={pageComparePath(project.id, p.id, p.shownVersion, p.version)}>
                    {t("wiki.history.compareWithCurrent")}
                  </Link>
                </Button>
                {canWrite ? (
                  <Button
                    ref={restoreTrigger}
                    size="xs"
                    variant="outline"
                    onClick={() =>
                      setRestore({
                        kind: "version",
                        pageId: p.id,
                        version: p.shownVersion,
                        title: p.title,
                      })
                    }
                  >
                    {t("wiki.history.restore")}
                  </Button>
                ) : null}
              </div>
            }
          >
            {t("wiki.newerVersion")}
          </Notice>
        ) : null}
        {hints.banner}
        {since !== null ? (
          <ReadSinceLine
            since={since}
            showing={marking}
            onShow={(on) => setShowSince(on ? sinceKey : null)}
            onDismiss={() => {
              setShowSince(null);
              readSince.dismiss();
            }}
          />
        ) : null}
        {p.declassified && can(project, "declassify") ? (
          <Notice tone="warning" title={t("wiki.declassified")}>
            {t("wiki.declassifiedExplain")}
          </Notice>
        ) : null}
      </header>
      <div ref={body} data-reading-page="" className="relative">
        {marking ? (
          <ReadSinceMarks
            projectId={project.id}
            page={p}
            since={since}
            resolve={resolve}
            // A comparison that fails (an older version since withheld) keeps the page readable.
            fallback={reading}
          />
        ) : (
          <>
            {reading}
            {passages.popover}
          </>
        )}
      </div>
      {passages.error ? <Notice tone="danger">{passages.error}</Notice> : null}
      {passages.overlay}
      {canWrite && !p.newerVersion ? (
        <div className="flex flex-wrap gap-2 border-t border-line pt-4">
          <Button variant="outline" size="sm" onClick={() => onEdit(p)}>
            {t("wiki.edit")}
          </Button>
          {can(project, "wiki.delete_page") ? (
            <Button variant="ghost" size="sm" onClick={() => remove.mutate()}>
              {t("wiki.delete")}
            </Button>
          ) : null}
        </div>
      ) : null}
      {remove.isError ? <Notice tone="danger">{remove.error?.message}</Notice> : null}
      {aside.dialog}
      <FolderNeighbours projectId={project.id} pageId={p.id} />
      <RestoreDialog
        projectId={project.id}
        target={restore}
        onClose={() => setRestore(null)}
        returnFocus={() => restoreTrigger.current}
      />
    </article>
  );
}

function Declassifications({ projectId }: { projectId: string }) {
  const list = useDeclassifications(projectId, true);
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const withdraw = useMutation({
    mutationFn: async (declassificationId: string) =>
      noContent(
        await api.api.projects[":projectId"].declassifications[
          ":declassificationId"
        ].withdraw.$post(
          { param: { projectId, declassificationId } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: wikiKeys.all(projectId) }),
  });
  if (list.isError) {
    return <Notice tone="danger">{list.error.message}</Notice>;
  }
  if (!list.data) {
    return <Loading />;
  }
  if (list.data.length === 0) {
    return <p className="text-sm text-fg-secondary">{t("wiki.declassifications.empty")}</p>;
  }
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
      {withdraw.isError ? <Notice tone="danger">{withdraw.error.message}</Notice> : null}
      <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised text-sm">
        {list.data.map((d) => (
          <li key={d.id} className="flex flex-col gap-1 px-4 py-3">
            <span className="font-medium text-fg">
              {d.title ?? t("wiki.declassifications.unreadable")} ·{" "}
              {t("wiki.version", { version: d.version })}
            </span>
            <span className="text-fg-secondary">
              {d.reason} ·{" "}
              {t("wiki.declassifications.row", {
                who: d.actorName ?? t("wiki.declassifications.someone"),
                at: formatDate(d.createdAt),
              })}
            </span>
            {d.withdrawnAt ? (
              <span className="text-xs text-fg-muted">
                {t("wiki.declassifications.withdrawn", { at: formatDate(d.withdrawnAt) })}
              </span>
            ) : (
              <Button
                size="xs"
                variant="outline"
                className="mt-1 self-start"
                onClick={() => withdraw.mutate(d.id)}
              >
                {t("wiki.declassifications.withdraw")}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ExportButton({ projectId }: { projectId: string }) {
  const { csrfToken } = useSession();
  const [exportId, setExportId] = useState<string | null>(null);
  const status = useWikiExport(projectId, exportId);
  const request = useMutation({
    mutationFn: async () =>
      ok(
        await api.api.projects[":projectId"].wiki.exports.$post(
          { param: { projectId } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: (created) => setExportId(created.id),
  });
  if (exportId && status.data?.status === "ready") {
    return (
      <Button asChild size="sm" variant="outline" title={t("wiki.exportReady")}>
        <a
          href={`/api/projects/${encodeURIComponent(projectId)}/wiki/exports/${encodeURIComponent(exportId)}/download`}
        >
          <Download aria-hidden="true" />
          <span className="sr-only xl:not-sr-only">{t("wiki.exportReady")}</span>
        </a>
      </Button>
    );
  }
  if (exportId) {
    return (
      <p className="text-xs text-fg-muted" aria-live="polite">
        {status.data?.status === "failed" ? t("wiki.exportFailed") : t("wiki.exporting")}
      </p>
    );
  }
  return (
    <Button
      size="sm"
      variant="ghost"
      className="text-fg-secondary"
      title={t("wiki.export")}
      disabled={request.isPending}
      onClick={() => request.mutate()}
    >
      <Download aria-hidden="true" />
      <span className="sr-only xl:not-sr-only">{t("wiki.export")}</span>
    </Button>
  );
}
